import {
	Input,
	Listbox,
	ListboxItem,
	ListboxSection,
	Modal,
	ModalBody,
	ModalContent,
	ModalHeader,
	CheckboxGroup,
	Checkbox,
	Chip,
	Link,
	Spinner,
} from '@heroui/react';
import { useState, useCallback, Key, FC, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { useAppSelector } from '../../core/rootHooks';
import { debounce } from 'lodash';
import { AppDispatch } from '../../core/store';
import { useAppDispatch } from '../../core/rootHooks';
import { clearRecentSearchesFromLocalStorage, getRecentSearchesFromLocalStorage } from '../../utils/helpers';
import {
	selectFilterSearchFormData,
	selectIsFetchedFilters,
	selectIsFetchedPortfolios,
	selectPortfolioSearchFormData,
	selectSubscribedOwnershipSearchFormData,
	selectSubscribedVesselSearchFormData,
	selectSubscribedSectorSearchFormData,
} from './searchSelectors';
import { selectPortSearchFormData, selectIsPortSearchBusy, selectSelectedPortGeometry } from '../port/portSelector';
import { selectVesselsLastSignalData } from '../main-map/mapSelectors';
import {
	IFilterSearchForm,
	IOwnershipSearchForm,
	IPortfolioSearchForm,
	IRecentSearchForm,
	ISearchDataForm,
	IVesselSearchForm,
	ISectorSearchForm,
} from './searchTypes';
import { IPortSearchForm } from '../port/portTypes';
import { ListboxWrapper } from './ListboxWrapper';
import { setSelectedVesselsForOutputRequest } from './searchSlice';
import {
	searchPortsRequest,
	clearSearchedPortsRequest,
	displayOutputByPortGeometryRequest,
	getPortGeometryByNameAndCountryRequest,
} from '../port/portSlice';
import { displayOutputByPortfolioIdRequest, getPortfoliosRequest } from '../portfolio/portfolioSlice';
import { displayOutputByFilterIdRequest, getFiltersRequest } from '../filter/filterSlice';
import { ISelectedVesselsFormData, IVesselFormData } from '../../shared/sharedTypes';
import { EOutputType, ESelectionType } from '../../shared/sharedEnums';
import SharedSpinner from '../../shared/components/spinner/SharedSpinner';
import { selectIsSubscriptionDataFetched } from '../../user-management/appSelectors';
import { filterGivenVesselsByFilterCriteriaForOutput } from './helper';
import { addSeascapeLogActivity } from '../../activity-logging/activityLogApi';
import { SeascapeActivityLogConstants } from '../../activity-logging/activityLogTypes';
import { trackEventWithContext, trackExceptionWithContext } from '../../services/application-insights/appInsights';
import {
	AppInsightsActions,
	AppInsightsComponents,
	AppInsightsErrorTypes,
} from '../../services/application-insights/appInsightsConstants';
import { showWarningAlert } from '../../services/globalAlertService';
import { resetCohortSource } from '../common/cohort/cohortSourceSlice';

export interface ISearchModalProps {
	isOpen: boolean;
	searchValue?: string;
	onOpenChange: () => void;
	handleOnSearchModalClose: () => void;
	clearAndResetNavigation: () => void;
}

const SearchModal: FC<ISearchModalProps> = (props) => {
	const dispatch = useAppDispatch<AppDispatch>();

	const ports = useAppSelector(selectPortSearchFormData); // Placeholder for ports selector
	const vessels = useAppSelector(selectSubscribedVesselSearchFormData);
	const portfolios = useAppSelector(selectPortfolioSearchFormData);
	const filters = useAppSelector(selectFilterSearchFormData);
	const ownerships = useAppSelector(selectSubscribedOwnershipSearchFormData);
	const sectors = useAppSelector(selectSubscribedSectorSearchFormData);

	const vesselsReady = useAppSelector(selectIsSubscriptionDataFetched);
	const portfoliosReady = useAppSelector(selectIsFetchedPortfolios);
	const filtersReady = useAppSelector(selectIsFetchedFilters);
	const isPortSearchBusy = useAppSelector(selectIsPortSearchBusy);

	// Selectors for port geometry and vessel signal data
	const selectedPortGeometry = useAppSelector(selectSelectedPortGeometry);
	const vesselsLastSignalData = useAppSelector(selectVesselsLastSignalData);

	const [filteredPorts, setFilteredPorts] = useState<IPortSearchForm[] | null>();
	const [filteredVessels, setFilteredVessels] = useState<IVesselSearchForm[] | null>();
	const [filteredPortfolios, setFilteredPortfolios] = useState<IPortfolioSearchForm[] | null>();
	const [filteredFilters, setFilteredFilters] = useState<IFilterSearchForm[] | null>();
	const [filteredOwnerships, setFilteredOwnerships] = useState<IOwnershipSearchForm[] | null>();
	const [filteredSectors, setFilteredSectors] = useState<ISectorSearchForm[] | null>();

	const [recentSearches, setRecentSearches] = useState<IRecentSearchForm[] | null>(
		getRecentSearchesFromLocalStorage().slice(0, 5) ?? null,
	);

	const [filterOptions, setFilterOptions] = useState<string[]>([
		'ports',
		'vessels',
		'portfolios',
		'filters',
		'ownerships',
		'sectors',
	]);

	// Initialize the Web Worker
	const searchWorker = useMemo(() => {
		try {
			const worker = new Worker(new URL('./searchWorker.ts', import.meta.url));

			// Track successful worker initialization
			trackEventWithContext(AppInsightsActions.SEARCH_WORKER_INIT, {
				component: AppInsightsComponents.SEARCH_WORKER,
				action: AppInsightsActions.SEARCH_WORKER_INIT,
				additionalData: { workerCreated: true },
			});

			return worker;
		} catch (error) {
			trackExceptionWithContext(error as Error, {
				component: AppInsightsComponents.SEARCH_WORKER,
				action: AppInsightsActions.SEARCH_WORKER_INIT,
				additionalData: {
					errorType: AppInsightsErrorTypes.SEARCH_WEB_WORKER_ERROR,
				},
			});
			throw error;
		}
	}, []);

	// Debounced search function
	const debouncedSearch = useCallback(
		(query: string) => {
			try {
				if (query.length >= 3) {
					debounce(() => {
						const searchData: ISearchDataForm = { ports, vessels, portfolios, filters, ownerships, sectors };
						if (query && (ports || vessels || portfolios || filters || ownerships || sectors)) {
							// Track search query submission
							trackEventWithContext(AppInsightsActions.SEARCH_QUERY_SUBMIT, {
								component: AppInsightsComponents.SEARCH_MODAL,
								action: AppInsightsActions.SEARCH_QUERY_SUBMIT,
								additionalData: {
									queryLength: query.length,
									hasPorts: !!ports?.length,
									hasVessels: !!vessels?.length,
									hasPortfolios: !!portfolios?.length,
									hasFilters: !!filters?.length,
									hasOwnerships: !!ownerships?.length,
									hasSectors: !!sectors?.length,
									filterOptions: filterOptions.join(','),
								},
							});

							// Trigger port search action if ports are ticked in filterOptions
							if (filterOptions.includes('ports')) {
								dispatch(searchPortsRequest({ portName: query }));
							}

							searchWorker.postMessage({ query, searchData });
						}
					}, 500)(); // Adjust debounce delay as needed
				} else {
					dispatch(clearSearchedPortsRequest());

					if (filteredPorts && filteredPorts.length > 0) {
						setFilteredPorts(null);
					}
					if (filteredVessels && filteredVessels.length > 0) {
						setFilteredVessels(null);
					}
					if (filteredPortfolios && filteredPortfolios.length > 0) {
						setFilteredPortfolios(null);
					}
					if (filteredFilters && filteredFilters.length > 0) {
						setFilteredFilters(null);
					}
					if (filteredOwnerships && filteredOwnerships.length > 0) {
						setFilteredOwnerships(null);
					}
					if (filteredSectors && filteredSectors.length > 0) {
						setFilteredSectors(null);
					}
				}
			} catch (error) {
				trackExceptionWithContext(error as Error, {
					component: AppInsightsComponents.SEARCH_MODAL,
					action: AppInsightsActions.SEARCH_QUERY_SUBMIT,
					additionalData: {
						errorType: AppInsightsErrorTypes.SEARCH_WEB_WORKER_ERROR,
						query,
					},
				});
			}
		},
		[
			ports,
			vessels,
			portfolios,
			filters,
			ownerships,
			sectors,
			searchWorker,
			filteredVessels,
			filteredPortfolios,
			filteredFilters,
			filteredOwnerships,
			filteredSectors,
			filterOptions,
			dispatch,
		],
	);

	useEffect(() => {
		if (!portfoliosReady && props.isOpen) {
			dispatch(getPortfoliosRequest());
		}
		if (!filtersReady && props.isOpen) {
			dispatch(getFiltersRequest());
		}
	}, [portfoliosReady, filtersReady, dispatch, props.isOpen]);

	// Load recent searches from localStorage on component mount
	useEffect(() => {
		const storedSearches = getRecentSearchesFromLocalStorage();
		if (storedSearches) {
			setRecentSearches(storedSearches);
		}
	}, [props.isOpen]);

	// Update filteredPorts when searched ports from the store change
	useEffect(() => {
		setFilteredPorts(ports);
	}, [ports]);

	// Trigger port vessel analysis when port geometry or vessel data changes
	useEffect(() => {
		// Only run when:
		// 1. Selection type is Port
		// 2. We have a port selected (selectionTypeObject exists and has portName and country)
		// 3. We have port geometry or vessel signal data
		if (selectedPortGeometry && vesselsLastSignalData && vesselsLastSignalData.features?.length > 0) {
			// Track the triggered analysis
			trackEventWithContext(AppInsightsActions.SEARCH_QUERY_SUBMIT, {
				component: AppInsightsComponents.SEARCH_MODAL,
				action: AppInsightsActions.SEARCH_QUERY_SUBMIT,
				additionalData: {
					trigger: 'port-data-change',
					portName: selectedPortGeometry?.properties.portName,
					country: selectedPortGeometry?.properties.country,
					hasGeometry: !!selectedPortGeometry,
					hasVesselData: !!vesselsLastSignalData,
				},
			});

			// Dispatch the saga to analyze port vessels
			dispatch(displayOutputByPortGeometryRequest({ portGeometry: selectedPortGeometry }));
		}
	}, [selectedPortGeometry, vesselsLastSignalData, dispatch]);

	// Handle messages from the Web Worker
	useEffect(() => {
		searchWorker.onmessage = function (e) {
			try {
				const results: ISearchDataForm = e.data;

				// Track successful search worker message
				trackEventWithContext(AppInsightsActions.SEARCH_WORKER_MESSAGE, {
					component: AppInsightsComponents.SEARCH_WORKER,
					action: AppInsightsActions.SEARCH_WORKER_MESSAGE,
					additionalData: {
						// portsCount: results.ports?.length || 0, // Ports are searched via separate action, so this count may not be relevant
						vesselsCount: results.vessels?.length || 0,
						portfoliosCount: results.portfolios?.length || 0,
						filtersCount: results.filters?.length || 0,
						ownershipsCount: results.ownerships?.length || 0,
						sectorsCount: results.sectors?.length || 0,
						totalResults:
							(results.ports?.length || 0) +
							(results.vessels?.length || 0) +
							(results.portfolios?.length || 0) +
							(results.filters?.length || 0) +
							(results.ownerships?.length || 0) +
							(results.sectors?.length || 0),
					},
				});

				// setFilteredPorts(results.ports || null); // Ports are handled via separate search action and may not be returned by the worker, so we set them in a separate useEffect that listens to the ports from the store
				setFilteredVessels(results.vessels || null);
				setFilteredPortfolios(results.portfolios || null);
				setFilteredFilters(results.filters || null);
				setFilteredOwnerships(results.ownerships || null);
				setFilteredSectors(results.sectors || null);
			} catch (error) {
				trackExceptionWithContext(error as Error, {
					component: AppInsightsComponents.SEARCH_WORKER,
					action: AppInsightsActions.SEARCH_WORKER_MESSAGE,
					additionalData: {
						errorType: AppInsightsErrorTypes.SEARCH_WEB_WORKER_ERROR,
					},
				});
			}
		};

		searchWorker.onerror = function (error) {
			trackExceptionWithContext(new Error(error.message), {
				component: AppInsightsComponents.SEARCH_WORKER,
				action: AppInsightsActions.SEARCH_WORKER_MESSAGE,
				additionalData: {
					errorType: AppInsightsErrorTypes.SEARCH_WEB_WORKER_ERROR,
					errorMessage: error.message,
				},
			});
		};

		return () => {
			searchWorker.terminate();
		};
	}, [searchWorker]);

	// Handle input change
	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		try {
			const searchValue = e.target.value;

			// Removed excessive input change tracking for performance
			// Search submissions are still tracked in debouncedSearch
			debouncedSearch(searchValue);
		} catch (error) {
			trackExceptionWithContext(error as Error, {
				component: AppInsightsComponents.SEARCH_MODAL,
				action: AppInsightsActions.SEARCH_INPUT_CHANGE,
				additionalData: {
					errorType: AppInsightsErrorTypes.SEARCH_INPUT_VALIDATION_ERROR,
					inputValue: e.target.value,
				},
			});
		}
	};

	const handleSelectedOption = (key: Key) => {
		try {
			//props.clearAndResetNavigation();
			let index = key.toString().indexOf('-');
			let splittedKeyArray = [key.toString().slice(0, index), key.toString().slice(index + 1)];
			const type = splittedKeyArray[0];

			// Track entity selection attempt
			trackEventWithContext(AppInsightsActions.SEARCH_ENTITY_SELECT, {
				component: AppInsightsComponents.SEARCH_MODAL,
				action: AppInsightsActions.SEARCH_ENTITY_SELECT,
				additionalData: {
					entityType: type,
					selectionKey: key.toString(),
				},
			});

			dispatch(resetCohortSource());

			if (type === 'port') {
				const portKey = splittedKeyArray[1]; // This contains: "name-country"
				// Split the portKey by '-' to get [name, country]
				const portParts = portKey.toString().split('-');
				const name = portParts[0];
				const country = portParts[1];

				dispatch(getPortGeometryByNameAndCountryRequest({ portName: name, country }));

				// Track successful port selection
				trackEventWithContext(AppInsightsActions.SEARCH_PORT_SELECT, {
					component: AppInsightsComponents.SEARCH_MODAL,
					action: AppInsightsActions.SEARCH_PORT_SELECT,
					additionalData: {
						portName: name,
						country: country,
					},
				});

				const payload = JSON.stringify({ Type: type, PortName: name, Country: country });
				const eventId = SeascapeActivityLogConstants.SEARCHED_ENTERED;
				addSeascapeLogActivity({ eventId, payload });
			} else if (type === 'vessel') {
				const imoNumber = splittedKeyArray[1];
				const subscribedMsiVessel = vessels?.find((vessel) => vessel.imoNumber.toString() === imoNumber);

				if (subscribedMsiVessel) {
					const outputSelection: ISelectedVesselsFormData = {
						selectedVesselsList: [
							{
								name: subscribedMsiVessel.name,
								imoNumber: subscribedMsiVessel.imoNumber,
								sectorName: subscribedMsiVessel.sector,
								companyName: subscribedMsiVessel.companyName,
							},
						] as IVesselFormData[],
						outputType: EOutputType.Vessel,
						selectionType: ESelectionType.Vessel,
						selectionName: `${subscribedMsiVessel.imoNumber} - ${subscribedMsiVessel.name}`,
						selectionTypeObject: {
							name: subscribedMsiVessel.name,
							imoNumber: subscribedMsiVessel.imoNumber,
							sectorName: subscribedMsiVessel.sector,
						} as IVesselFormData,
					};

					dispatch(setSelectedVesselsForOutputRequest({ outputSelection }));

					// Track successful vessel selection
					trackEventWithContext(AppInsightsActions.SEARCH_VESSEL_SELECT, {
						component: AppInsightsComponents.SEARCH_MODAL,
						action: AppInsightsActions.SEARCH_VESSEL_SELECT,
						additionalData: {
							vesselName: subscribedMsiVessel.name,
							imoNumber: subscribedMsiVessel.imoNumber,
							sector: subscribedMsiVessel.sector,
						},
					});

					const payload = JSON.stringify({ Type: type, ImoNumber: imoNumber, Name: outputSelection.selectionTypeObject?.name });
					const eventId = SeascapeActivityLogConstants.SEARCHED_ENTERED;
					addSeascapeLogActivity({ eventId, payload });
				} else {
					// Track vessel not found error
					trackExceptionWithContext(new Error('Vessel not found'), {
						component: AppInsightsComponents.SEARCH_MODAL,
						action: AppInsightsActions.SEARCH_VESSEL_SELECT,
						additionalData: {
							errorType: AppInsightsErrorTypes.SEARCH_VESSEL_NOT_FOUND_ERROR,
							imoNumber,
							searchKey: key.toString(),
						},
					});
					showWarningAlert('Vessel not found');
				}
			} else if (type === 'portfolio') {
				const portfolioId = splittedKeyArray[1];
				dispatch(displayOutputByPortfolioIdRequest({ id: parseInt(portfolioId) }));

				const portfolio = filters?.find((f) => f.id.toString() === portfolioId);

				// Track successful portfolio selection
				trackEventWithContext(AppInsightsActions.SEARCH_PORTFOLIO_SELECT, {
					component: AppInsightsComponents.SEARCH_MODAL,
					action: AppInsightsActions.SEARCH_PORTFOLIO_SELECT,
					additionalData: {
						portfolioId,
						portfolioName: portfolio?.name,
						scope: portfolio?.scope,
					},
				});

				const payload = JSON.stringify({ Type: type, PortfolioId: portfolioId, Name: portfolio?.name, Scope: portfolio?.scope });

				const eventId = SeascapeActivityLogConstants.SEARCHED_ENTERED;
				addSeascapeLogActivity({ eventId, payload });
			} else if (type === 'filter') {
				const filterId = splittedKeyArray[1];
				dispatch(displayOutputByFilterIdRequest({ id: parseInt(filterId) }));

				const filter = filters?.find((f) => f.id.toString() === filterId);

				// Track successful filter selection
				trackEventWithContext(AppInsightsActions.SEARCH_FILTER_SELECT, {
					component: AppInsightsComponents.SEARCH_MODAL,
					action: AppInsightsActions.SEARCH_FILTER_SELECT,
					additionalData: {
						filterId,
						filterName: filter?.name,
						scope: filter?.scope,
					},
				});

				const payload = JSON.stringify({ Type: type, FilterId: filterId, Name: filter?.name, Scope: filter?.scope });

				const eventId = SeascapeActivityLogConstants.SEARCHED_ENTERED;
				addSeascapeLogActivity({ eventId, payload });
			} else if (type === 'ownership') {
				const name = splittedKeyArray[1];
				const companyFilterCriteria = { type: ESelectionType.Ownership, value: name };
				const companyOutput = filterGivenVesselsByFilterCriteriaForOutput(vessels, companyFilterCriteria);
				dispatch(setSelectedVesselsForOutputRequest({ outputSelection: companyOutput }));

				// Track successful ownership selection
				trackEventWithContext(AppInsightsActions.SEARCH_OWNERSHIP_SELECT, {
					component: AppInsightsComponents.SEARCH_MODAL,
					action: AppInsightsActions.SEARCH_OWNERSHIP_SELECT,
					additionalData: {
						ownershipName: name,
						vesselCount: companyOutput.selectedVesselsList?.length || 0,
					},
				});

				const payload = JSON.stringify({ Type: type, Ownership: name });
				const eventId = SeascapeActivityLogConstants.SEARCHED_ENTERED;
				addSeascapeLogActivity({ eventId, payload });
			} else if (type === 'sector') {
				const name = splittedKeyArray[1];
				const sectorFilterCriteria = { type: ESelectionType.Sector, value: name };
				const sectorOutput = filterGivenVesselsByFilterCriteriaForOutput(vessels, sectorFilterCriteria);
				dispatch(setSelectedVesselsForOutputRequest({ outputSelection: sectorOutput }));

				// Track successful sector selection
				trackEventWithContext(AppInsightsActions.SEARCH_SECTOR_SELECT, {
					component: AppInsightsComponents.SEARCH_MODAL,
					action: AppInsightsActions.SEARCH_SECTOR_SELECT,
					additionalData: {
						sectorName: name,
						vesselCount: sectorOutput.selectedVesselsList?.length || 0,
					},
				});

				const payload = JSON.stringify({ Type: type, Sector: name });
				const eventId = SeascapeActivityLogConstants.SEARCHED_ENTERED;
				addSeascapeLogActivity({ eventId, payload });
			} else {
				return;
			}

			setFilteredPorts(undefined);
			setFilteredVessels(undefined);
			setFilteredPortfolios(undefined);
			setFilteredFilters(undefined);
			setFilteredOwnerships(undefined);
			setFilteredSectors(undefined);
			props.handleOnSearchModalClose();
		} catch (error) {
			trackExceptionWithContext(error as Error, {
				component: AppInsightsComponents.SEARCH_MODAL,
				action: AppInsightsActions.SEARCH_ENTITY_SELECT,
				additionalData: {
					errorType: AppInsightsErrorTypes.SEARCH_SELECTION_ERROR,
					selectionKey: key.toString(),
				},
			});
		}
	};

	const handleClearRecentSearch = () => {
		try {
			// Track recent search clear action
			trackEventWithContext(AppInsightsActions.SEARCH_RECENT_CLEAR, {
				component: AppInsightsComponents.SEARCH_MODAL,
				action: AppInsightsActions.SEARCH_RECENT_CLEAR,
				additionalData: {
					recentSearchCount: recentSearches?.length || 0,
				},
			});

			clearRecentSearchesFromLocalStorage();
			setRecentSearches(null);
		} catch (error) {
			trackExceptionWithContext(error as Error, {
				component: AppInsightsComponents.SEARCH_MODAL,
				action: AppInsightsActions.SEARCH_RECENT_CLEAR,
				additionalData: {
					errorType: AppInsightsErrorTypes.SEARCH_RECENT_STORAGE_ERROR,
				},
			});
		}
	};

	const renderFilterOptions = () => {
		return (
			<div className="flex items-center gap-4">
				<div className="text-xs font-semibold">Search in </div>
				<CheckboxGroup
					size="sm"
					orientation="horizontal"
					defaultValue={filterOptions}
					onChange={(value) => {
						try {
							// Track search options change
							trackEventWithContext(AppInsightsActions.SEARCH_OPTIONS_CHANGE, {
								component: AppInsightsComponents.SEARCH_MODAL,
								action: AppInsightsActions.SEARCH_OPTIONS_CHANGE,
								additionalData: {
									previousOptions: filterOptions.join(','),
									newOptions: value.join(','),
									optionCount: value.length,
								},
							});

							setFilterOptions(value);
						} catch (error) {
							trackExceptionWithContext(error as Error, {
								component: AppInsightsComponents.SEARCH_MODAL,
								action: AppInsightsActions.SEARCH_OPTIONS_CHANGE,
								additionalData: {
									errorType: AppInsightsErrorTypes.SEARCH_MODAL_INTERACTION_ERROR,
									filterOptions: value.join(','),
								},
							});
						}
					}}
					radius="sm"
					isInvalid={filterOptions.length === 0}
				>
					<Checkbox classNames={{ label: 'text-xs text-msi-midblue-color-500 font-semibold' }} value="ports">
						Ports
					</Checkbox>
					<Checkbox classNames={{ label: 'text-xs text-msi-midblue-color-500 font-semibold' }} value="vessels">
						Vessels
					</Checkbox>
					<Checkbox classNames={{ label: 'text-xs text-msi-midblue-color-500 font-semibold' }} value="ownerships">
						Ownerships
					</Checkbox>
					<Checkbox classNames={{ label: 'text-xs text-msi-midblue-color-500 font-semibold' }} value="sectors">
						Sectors
					</Checkbox>
					<Checkbox classNames={{ label: 'text-xs text-msi-midblue-color-500 font-semibold' }} value="portfolios">
						Portfolios
					</Checkbox>
					<Checkbox classNames={{ label: 'text-xs text-msi-midblue-color-500 font-semibold' }} value="filters">
						Filters
					</Checkbox>
				</CheckboxGroup>
			</div>
		);
	};

	const topContent = useMemo(() => {
		if (recentSearches && (recentSearches?.length ?? 0) > 0)
			return (
				<div className="grid grid-cols-2 ml-1">
					<div className="grid justify-items-start text-xs">Recent Search</div>
					<div className="grid justify-items-end mr-3">
						<Link className="text-xs cursor-pointer" onPress={handleClearRecentSearch}>
							Clear
						</Link>
					</div>
				</div>
			);
	}, [recentSearches]);

	const searchIsBusy = !vesselsReady || !portfoliosReady || !filtersReady;
	const anyResults =
		(filteredVessels?.length ?? 0) > 0 ||
		(filteredPortfolios?.length ?? 0) > 0 ||
		(filteredFilters?.length ?? 0) > 0 ||
		(filteredOwnerships?.length ?? 0) > 0 ||
		(filteredSectors?.length ?? 0) > 0;

	return (
		<Modal
			className="rounded"
			isOpen={props.isOpen}
			placement={'top'}
			onOpenChange={props.onOpenChange}
			disableAnimation
			hideCloseButton
			size="xl"
		>
			<ModalContent>
				{() => (
					<>
						<ModalHeader className="flex flex-col space-y-2 items-center pb-0">
							<Input
								isReadOnly={filterOptions.length === 0}
								autoFocus
								isInvalid={filterOptions.length === 0}
								isClearable={true}
								onClear={() => debouncedSearch('')}
								classNames={{
									base: 'max-w-full sm:max-w-[32rem] sm:h-6 mb-5',
									mainWrapper: 'h-full',
									input: 'text-small',
									inputWrapper:
										'h-full text-default-500 rounded border group-data-[focus-visible=true]:ring-1 group-data-[focus-visible=true]:ring-offset-1 group-data-[focus-visible=true]:ring-msi-black-color',
									label: 'font-normal',
									clearButton: 'text-lg flex items-center',
								}}
								placeholder={filterOptions.length === 0 ? "Please select at least one 'Search in' option" : 'Search'}
								size="sm"
								startContent={<FontAwesomeIcon icon={faSearch} />}
								type="text"
								onChange={handleInputChange}
							/>
							{renderFilterOptions()}
						</ModalHeader>
						<ModalBody className="p-2">
							<ListboxWrapper>
								<Listbox
									aria-label="Listbox menu with ports, sectors, vessels, portfolios and filters search results"
									onAction={(key) => handleSelectedOption(key)}
									classNames={{
										list: 'max-h-[400px] overflow-x-auto scrollbar-thin hover:rounded',
									}}
									disabledKeys={[
										'port-not-found',
										'vessel-not-found',
										'portfolio-not-found',
										'filter-not-found',
										'ownership-not-found',
										'sector-not-found',
									]}
									topContent={anyResults ? null : topContent}
									className="w-full"
								>
									<ListboxSection
										hidden={!recentSearches || (recentSearches.length ?? 0) === 0 || anyResults}
										showDivider
										className="pr-2"
									>
										{recentSearches && (recentSearches.length ?? 0) > 0 ? (
											recentSearches.map((item) =>
												item.type === 'Port' ? (
													<ListboxItem
														key={'port-' + item.name + '-' + item.country}
														className={`cursor-default rounded h-6 text-small data-selectable:focus:bg-transparent`}
														classNames={{ title: 'flex justify-between items-center' }}
														textValue={`${item.name}`}
													>
														<div className="w-full rounded-none">
															<div className="grid gap-2 text-sm text-gray-700">
																<div className="grid grid-cols-8 gap-x-4 items-center justify-center whitespace-nowrap">
																	<span className="col-span-5 truncate overflow-hidden text-ellipsis" title={` ${item.name}`}>
																		{item.name}
																	</span>
																	<span className="col-span-1 text-xs font-semibold justify-self-end">Port</span>
																	<Chip
																		className="col-span-2 flex-none justify-self-end text-[10px] rounded h-4 bg-msi-midblue-color-500 text-msi-white-color ml-1"
																		size="sm"
																	>
																		{item.country}
																	</Chip>
																</div>
															</div>
														</div>
													</ListboxItem>
												) : item.type === 'Vessel' ? (
													<ListboxItem
														key={'vessel-' + item.imoNumber}
														className={`cursor-default rounded h-6 text-small data-selectable:focus:bg-transparent`}
														classNames={{ title: 'flex justify-between items-center' }}
														textValue={`${item.imoNumber} - ${item.name}`}
													>
														<div className="w-full rounded-none">
															<div className="grid gap-2 text-sm text-gray-700">
																<div className="grid grid-cols-8 gap-x-4 items-center justify-center whitespace-nowrap">
																	<span
																		className="col-span-5 truncate overflow-hidden text-ellipsis"
																		title={`${item.imoNumber} - ${item.name}`}
																	>
																		{item.imoNumber} - {item.name}
																	</span>
																	<span className="col-span-1 text-xs font-semibold justify-self-end">Vessel</span>
																	<Chip
																		className="col-span-2 flex-none justify-self-end text-[10px] rounded h-4 bg-msi-midblue-color-500 text-msi-white-color ml-1"
																		size="sm"
																	>
																		{item.sector}
																	</Chip>
																</div>
															</div>
														</div>
													</ListboxItem>
												) : item.type === 'Portfolio' ? (
													<ListboxItem
														key={'portfolio-' + item.id}
														className={`cursor-default rounded h-6 text-small data-selectable:focus:bg-transparent`}
														classNames={{ title: 'flex justify-between items-center' }}
														textValue={`${item.id} - ${item.name}`}
													>
														<div className="w-full  rounded-none">
															<div className="grid gap-2 text-sm text-gray-700">
																<div className="grid grid-cols-8 gap-x-4 items-center justify-center whitespace-nowrap">
																	<span className="col-span-5 truncate overflow-hidden text-ellipsis" title={item.name}>
																		{item.name}
																	</span>
																	<span className="col-span-1 text-xs font-semibold justify-self-end">Portfolio</span>
																	<Chip
																		className={`col-span-2 flex-none justify-self-end text-[10px] rounded h-4 ${item.isOwnPortfolio ? 'bg-success-500' : 'bg-warning-500'} text-msi-white-color ml-1`}
																		size="sm"
																	>
																		{item.isOwnPortfolio ? 'Personal' : 'Team'}
																	</Chip>
																</div>
															</div>
														</div>
													</ListboxItem>
												) : item.type === 'Filter' ? (
													<ListboxItem
														key={'filter-' + item.id}
														className={`cursor-default rounded h-6 text-small data-selectable:focus:bg-transparent`}
														classNames={{ title: 'flex justify-between items-center' }}
														textValue={`${item.id} - ${item.name}`}
													>
														<div className="w-full  rounded-none">
															<div className="grid gap-2 text-sm text-gray-700">
																<div className="grid grid-cols-8 gap-x-4 items-center justify-center whitespace-nowrap">
																	<span className="col-span-5 truncate overflow-hidden text-ellipsis" title={item.name}>
																		{item.name}
																	</span>
																	<span className="col-span-1 text-xs font-semibold justify-self-end">Filter</span>
																	<Chip
																		className={`col-span-2 flex-none justify-self-end text-[10px] rounded h-4 ${item.isOwnFilter ? 'bg-success-500' : 'bg-warning-500'} text-msi-white-color ml-1`}
																		size="sm"
																	>
																		{item.isOwnFilter ? 'Personal' : 'Team'}
																	</Chip>
																</div>
															</div>
														</div>
													</ListboxItem>
												) : item.type === 'Ownership' ? (
													<ListboxItem
														key={'ownership-' + item.name}
														className={`cursor-default rounded h-6 text-small data-selectable:focus:bg-transparent`}
														classNames={{ title: 'flex justify-between items-center' }}
														textValue={`${item.id} - ${item.name}`}
													>
														<div className="w-full  rounded-none">
															<div className="grid gap-2 text-sm text-gray-700">
																<div className="grid grid-cols-8 gap-x-4 items-center justify-center whitespace-nowrap">
																	<span className="col-span-5 truncate overflow-hidden text-ellipsis" title={item.name}>
																		{item.name}
																	</span>
																	<span className="col-span-1 text-xs font-semibold justify-self-end">Ownership</span>
																	<Chip
																		className="col-span-2 flex-none justify-self-end text-[10px] rounded h-4 bg-msi-darkblue-color text-msi-white-color ml-1"
																		size="sm"
																	>
																		Company
																	</Chip>
																</div>
															</div>
														</div>
													</ListboxItem>
												) : (
													<ListboxItem
														key={'sector-' + item.name}
														className={`cursor-default rounded h-6 text-small data-selectable:focus:bg-transparent`}
														classNames={{ title: 'flex justify-between items-center' }}
														textValue={`${item.id} - ${item.name}`}
													>
														<div className="w-full rounded-none">
															<div className="grid gap-2 text-sm text-gray-700">
																<div className="grid grid-cols-8 gap-x-4 items-center justify-center whitespace-nowrap">
																	<span className="col-span-5 truncate overflow-hidden text-ellipsis" title={item.name}>
																		{item.name}
																	</span>
																	<span className="col-span-1 text-xs font-semibold justify-self-end">Sector</span>
																	<Chip
																		className="col-span-2 flex-none justify-self-end text-[10px] rounded h-4 bg-msi-midblue-color-500 text-msi-white-color ml-1"
																		size="sm"
																	>
																		{item.name}
																	</Chip>
																</div>
															</div>
														</div>
													</ListboxItem>
												),
											)
										) : (
											<ListboxItem
												isReadOnly
												className="text-small h-6 rounded"
												hideSelectedIcon
												key="recent-search-not-found"
												textValue="No vessel found"
											>
												No vessel found
											</ListboxItem>
										)}
									</ListboxSection>
									<ListboxSection hidden={!filterOptions.includes('ports')} title="Ports" showDivider className="pr-2">
										{isPortSearchBusy ? (
											<ListboxItem key="port-search-busy" textValue="Searching ports...">
												<div className="flex items-center space-x-2">
													<Spinner size="sm" className="text-msi-midblue-color-600" />
													<span>Searching ports...</span>
												</div>
											</ListboxItem>
										) : filteredPorts && filteredPorts.length > 0 ? (
											filteredPorts.map((item) => (
												<ListboxItem
													key={'port-' + item.name + '-' + item.country}
													className={`cursor-default rounded h-6 text-small data-selectable:focus:bg-transparent`}
													classNames={{ title: 'flex justify-between items-center' }}
													textValue={`port - ${item.name}`}
												>
													<div className="flex-none">{item.name}</div>
													<Chip
														className="flex-none text-[10px] rounded h-4 bg-msi-chart-midgreen text-msi-white-color"
														size="sm"
													>
														{item.country}
													</Chip>
												</ListboxItem>
											))
										) : (
											<ListboxItem key="port-not-found" textValue="No port found">
												No port found
											</ListboxItem>
										)}
									</ListboxSection>
									<ListboxSection hidden={!filterOptions.includes('vessels')} title="Vessels" showDivider className="pr-2">
										{filteredVessels && filteredVessels.length > 0 ? (
											filteredVessels.map((item) => (
												<ListboxItem
													key={'vessel-' + item.imoNumber}
													className={`cursor-default rounded h-6 text-small data-selectable:focus:bg-transparent`}
													classNames={{ title: 'flex justify-between items-center' }}
													textValue={`${item.imoNumber} - ${item.name}`}
												>
													<div className="flex-none">
														{item.imoNumber} - {item.name}
													</div>
													<Chip
														className="flex-none text-[10px] rounded h-4 bg-msi-midblue-color-600 text-msi-white-color"
														size="sm"
													>
														{item.sector}
													</Chip>
												</ListboxItem>
											))
										) : (
											<ListboxItem key="vessel-not-found" textValue="No vessel found">
												No vessel found
											</ListboxItem>
										)}
									</ListboxSection>
									<ListboxSection hidden={!filterOptions.includes('ownerships')} title="Ownerships" showDivider className="pr-2">
										{filteredOwnerships && filteredOwnerships.length > 0 ? (
											filteredOwnerships.map((item) => (
												<ListboxItem
													key={'ownership-' + item.name}
													className={`cursor-default rounded h-6 text-small data-selectable:focus:bg-transparent`}
													classNames={{ title: 'flex justify-between items-center' }}
													textValue={`ownerhips - ${item.name}`}
												>
													<div className="flex-none">{item.name}</div>
													<Chip
														className="flex-none text-[10px] rounded h-4 bg-msi-orange-color-600 text-msi-white-color"
														size="sm"
													>
														Company
													</Chip>
												</ListboxItem>
											))
										) : (
											<ListboxItem key="ownership-not-found" textValue="No ownership found">
												No ownership found
											</ListboxItem>
										)}
									</ListboxSection>
									<ListboxSection hidden={!filterOptions.includes('sectors')} title="Sectors" showDivider className="pr-2">
										{filteredSectors && filteredSectors.length > 0 ? (
											filteredSectors.map((item) => (
												<ListboxItem
													key={'sector-' + item.name}
													className={`cursor-default rounded h-6 text-small data-selectable:focus:bg-transparent`}
													classNames={{ title: 'flex justify-between items-center' }}
													textValue={`sector - ${item.name}`}
												>
													<div className="flex-none">{item.name}</div>
													<Chip
														className="flex-none text-[10px] rounded h-4 bg-msi-darkblue-color-600 text-msi-white-color"
														size="sm"
													>
														Sector
													</Chip>
												</ListboxItem>
											))
										) : (
											<ListboxItem key="sector-not-found" textValue="No sector found">
												No sector found
											</ListboxItem>
										)}
									</ListboxSection>
									<ListboxSection hidden={!filterOptions.includes('portfolios')} title="Portfolios" showDivider className="pr-2">
										{filteredPortfolios && filteredPortfolios.length > 0 ? (
											filteredPortfolios.map((item) => (
												<ListboxItem
													key={'portfolio-' + item.id}
													className={`cursor-default rounded h-6 text-small data-selectable:focus:bg-transparent`}
													classNames={{ title: 'flex justify-between items-center' }}
													textValue={`${item.id} - ${item.name}`}
												>
													<div className="flex-none">{item.name}</div>
													<Chip
														className={`flex-none text-[10px] rounded h-4 ${item.isOwnPortfolio ? 'bg-success-500' : 'bg-warning-500'} text-msi-white-color`}
														size="sm"
													>
														{item.isOwnPortfolio ? 'Personal' : 'Team'}
													</Chip>
												</ListboxItem>
											))
										) : (
											<ListboxItem key="portfolio-not-found" textValue="No portfolio found">
												No portfolio found
											</ListboxItem>
										)}
									</ListboxSection>
									<ListboxSection hidden={!filterOptions.includes('filters')} title="Filters" className="pr-2">
										{filteredFilters && filteredFilters.length > 0 ? (
											filteredFilters.map((item) => (
												<ListboxItem
													key={'filter-' + item.id}
													className={`cursor-default rounded h-6 text-small data-selectable:focus:bg-transparent`}
													classNames={{ title: 'flex justify-between items-center' }}
													textValue={`${item.id} - ${item.name}`}
												>
													<div className="flex-none">{item.name}</div>
													<Chip
														className={`flex-none text-[10px] rounded h-4 ${item.isOwnFilter ? 'bg-success-500' : 'bg-warning-500'} text-msi-white-color`}
														size="sm"
													>
														{item.isOwnFilter ? 'Personal' : 'Team'}
													</Chip>
												</ListboxItem>
											))
										) : (
											<ListboxItem key="filter-not-found" textValue="No filter found">
												No filter found
											</ListboxItem>
										)}
									</ListboxSection>
								</Listbox>
							</ListboxWrapper>
							<SharedSpinner size="sm" textSize="text-small" isBusy={searchIsBusy} label="Loading..." />
						</ModalBody>
					</>
				)}
			</ModalContent>
		</Modal>
	);
};

export default SearchModal;
